import type { EditionSummary } from '@koderek/shared';
import { Center, Loader, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { Navigate } from 'react-router';
import { ErrorAlert } from '../components/ErrorAlert';
import { IconBulb, IconMapPin, IconSchool, IconSparkles, IconUsers } from '../components/icons';
import { useMe } from '../features/auth/session';
import { EditionCard } from '../features/editions/EditionCard';
import { formatDate } from '../features/editions/format';
import { useEditions } from '../features/editions/queries';
import { PartnersSection } from '../features/site/partners';
import { SystemStatus } from '../features/status/SystemStatus';
import { t } from '../texts/pl';
import classes from './HomePage.module.css';

/**
 * US-104 AC-1, AC-2: aktywne edycje (menu „Edycje”); gdy jest dokładnie jedna – od razu jej strona. Przy kilku – lista
 * z częścią powitalną.
 */
export function HomePage() {
  const editions = useEditions();
  const me = useMe().data;
  const only = editions.data?.editions.length === 1 ? editions.data.editions[0] : undefined;
  if (only) return <Navigate to={`/edycje/${only.slug}`} replace />;
  return (
    <Stack gap="xl">
      <div>
        <Text className={classes.eyebrow}>{me ? t.home.hello(me.firstName) : t.home.eyebrow}</Text>
        <Title order={1} className={classes.headline}>
          {t.home.headline}
        </Title>
        <Text mt="xs" c="dimmed">
          {t.home.intro}
        </Text>
      </div>
      <div className={classes.columns}>
        <EditionCards editions={editions} />
        <div className={classes.promo}>
          <IconSparkles size={32} />
          <Title order={2} className={classes.promoTitle}>
            {t.home.promoTitle}
          </Title>
          <Text className={classes.promoText}>{t.home.promoText}</Text>
        </div>
      </div>
      <Topics />
      <PartnersSection />
    </Stack>
  );
}

/** Edycje pogrupowane po dniu (lista z API jest już po dacie, w dniu – w kolejności ustalonej przez admina). */
function byDay(editions: EditionSummary[]): EditionSummary[][] {
  const groups = new Map<string, EditionSummary[]>();
  for (const e of editions) groups.set(e.date, [...(groups.get(e.date) ?? []), e]);
  return [...groups.values()];
}

/**
 * Kilka edycji tego samego dnia (jedna konferencja w kilku miastach – osoba wybiera jedno miasto,
 * w jego obrębie może łączyć miejsca, BR-17): wspólny nagłówek z datą
 * i lista szerokich kart miast jedna pod drugą (przy kilku miastach siatka dawała nierówne rzędy). Pojedyncza edycja danego
 * dnia – zwykła karta.
 */
function EditionCards({ editions }: { editions: ReturnType<typeof useEditions> }) {
  return (
    <Stack gap="md">
      {editions.isPending ? (
        <Center py="lg">
          <Loader aria-label="Ładowanie" />
        </Center>
      ) : null}
      {editions.isError ? <ErrorAlert error={editions.error} /> : null}
      {byDay(editions.data?.editions ?? []).map((day) =>
        day.length === 1 ? (
          <EditionCard key={day[0]!.id} edition={day[0]!} />
        ) : (
          <Stack key={day[0]!.date} gap="sm">
            <div>
              <Title order={2} size="h3">
                {formatDate(day[0]!.date)}
              </Title>
              {day.some((e) => e.registered) && (
                <Text c="dimmed" size="sm">
                  {t.home.enrolledIn(day.filter((e) => e.registered).map((e) => e.city).join(', '))}
                </Text>
              )}
            </div>
            <Stack gap="md">
              {day.map((edition) => (
                <EditionCard key={edition.id} edition={edition} inDay />
              ))}
            </Stack>
          </Stack>
        ),
      )}
      {editions.data && !editions.data.editions.length ? (
        <>
          <Text c="dimmed">{t.home.empty}</Text>
          <SystemStatus />
        </>
      ) : null}
    </Stack>
  );
}

const TOPIC_STYLE = [
  { icon: IconSchool, className: classes.topicTeal },
  { icon: IconBulb, className: classes.topicOrange },
  { icon: IconUsers, className: classes.topicViolet },
  { icon: IconMapPin, className: classes.topicPink },
];

function Topics() {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
      {t.home.topics.map((topic, i) => {
        const style = TOPIC_STYLE[i % TOPIC_STYLE.length]!;
        return (
          <div key={topic.title} className={`${classes.topic} ${style.className}`}>
            <span className={classes.topicIcon}>
              <style.icon size={24} />
            </span>
            <Text fw={800} mt="sm">
              {topic.title}
            </Text>
            <Text size="sm" mt={4}>
              {topic.text}
            </Text>
          </div>
        );
      })}
    </SimpleGrid>
  );
}
